import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  IconButton,
} from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import { FilesFormFields } from "./filesValidationSchema";
import { FileReader } from "..";
import { useFileInputUploader } from "../../hooks";

export interface FileRowProps {
  index: number;
  control: Control<FilesFormFields>;
  setValue: UseFormSetValue<FilesFormFields>;
  onRemove: () => void;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * One row of `FilesForm`'s `useFieldArray` — same per-row upload pattern `CertificationRow.tsx`
 * (Task 9) built: `FileUploaderDropContainer`/`FileUploaderItem` wired to its OWN
 * `useFileInputUploader` instance, bound via a bare `Controller` on `fileURL` (the upload UI IS
 * the control, no `TextField` fits it). `FileReader` renders a download link once a row already
 * has a `fileURL` and nothing is mid-upload, matching the pre-Carbon card's own
 * `<FileReader fileKey={file.fileURL} />`.
 *
 * `setValue` (not just `control`) is threaded down from `FilesForm.tsx` for ONE reason: the
 * pre-Carbon form auto-filled `newFile.name` from the uploaded `File`'s own `.name` the moment an
 * upload completed, and never exposed a `name` text input for the user to type into instead. This
 * row reproduces that exactly — on a successful upload it writes `files.${index}.name` directly,
 * the one field this row's own JSX never renders a control for.
 */
const FileRow = ({ index, control, setValue, onRemove }: FileRowProps) => {
  const { t } = useTranslation();
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const { mutate: uploadFile, isPending: isUploading } = useFileInputUploader();

  return (
    <div className="files-form__row">
      <Controller
        name={`files.${index}.fileURL`}
        control={control}
        render={({ field, fieldState }) => (
          <div className="files-form__row-upload">
            <FileUploaderDropContainer
              labelText={
                isUploading
                  ? t("cta.uploading")
                  : t("companyProfile.uploadTaxDocument")
              }
              onAddFiles={(_event, { addedFiles }) => {
                const file = addedFiles[0];
                if (!file) return;

                if (file.size > MAX_FILE_SIZE_BYTES) {
                  setFileError(t("companyProfile.fileTooLarge"));
                  return;
                }

                setFileError("");
                setPendingFileName(file.name);
                setValue(`files.${index}.name`, file.name, {
                  shouldDirty: true,
                });
                uploadFile(file, {
                  onSuccess: (url: string) => field.onChange(url),
                });
              }}
            />
            {pendingFileName && (
              <FileUploaderItem
                name={pendingFileName}
                status={isUploading ? "uploading" : "complete"}
                onDelete={() => {
                  setPendingFileName(null);
                  field.onChange("");
                }}
              />
            )}
            {field.value && !pendingFileName && (
              <FileReader fileKey={field.value} text={t("cta.download")} />
            )}
            {(fieldState.error || fileError) && (
              <p className="files-form__row-error">
                {fieldState.error?.message || fileError}
              </p>
            )}
          </div>
        )}
      />

      <div className="files-form__row-actions">
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default FileRow;
